import React, { useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, Line, ComposedChart } from "recharts";
import { AlertTriangle, TrendingUp, TrendingDown, CheckCircle, Target } from "lucide-react";

const mesesLabel = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];
const mesesKeys = ["valor_jan", "valor_fev", "valor_mar", "valor_abr", "valor_mai", "valor_jun", 
                   "valor_jul", "valor_ago", "valor_set", "valor_out", "valor_nov", "valor_dez"];

export default function ComparativoOrcado({ orcamentos, lancamentos, custosAlocados, ano }) {
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);
  };

  // Calcular realizado por categoria e mês
  const realizadoPorCategoria = useMemo(() => {
    const resultado = {};

    // Processar lançamentos
    lancamentos.forEach(l => {
      const dataLanc = new Date(l.data);
      if (dataLanc.getFullYear() !== ano) return;
      
      const mes = dataLanc.getMonth();
      const cat = l.categoria || "Outros";
      const tipo = l.tipo;
      const key = `${tipo}_${cat}`;

      if (!resultado[key]) {
        resultado[key] = { tipo, categoria: cat, meses: Array(12).fill(0), total: 0 };
      }
      resultado[key].meses[mes] += l.valor || 0;
      resultado[key].total += l.valor || 0;
    });

    // Processar custos alocados
    custosAlocados.forEach(c => {
      const dataC = new Date(c.data);
      if (dataC.getFullYear() !== ano) return;
      
      const mes = dataC.getMonth();
      const cat = c.categoria || "Outros";
      const key = `despesa_${cat}`;

      if (!resultado[key]) {
        resultado[key] = { tipo: "despesa", categoria: cat, meses: Array(12).fill(0), total: 0 };
      }
      resultado[key].meses[mes] += c.valor || 0;
      resultado[key].total += c.valor || 0;
    });

    return resultado;
  }, [lancamentos, custosAlocados, ano]);

  // Comparativo por orçamento
  const comparativos = useMemo(() => {
    return orcamentos
      .filter(o => o.ano === ano && o.ativo !== false)
      .map(orc => {
        const keyRealizado = `${orc.tipo}_${orc.categoria}`;
        const realizado = realizadoPorCategoria[keyRealizado] || { meses: Array(12).fill(0), total: 0 };

        const orcadoMensal = mesesKeys.map(k => orc[k] || 0);
        const realizadoMensal = realizado.meses;

        const totalOrcado = orc.valor_total || 0;
        const totalRealizado = realizado.total;
        
        let desvio = 0;
        if (totalOrcado > 0) {
          desvio = ((totalRealizado - totalOrcado) / totalOrcado) * 100;
        }

        // Para despesas, positivo é ruim (gastou mais). Para receitas, negativo é ruim (recebeu menos)
        const limiteAlerta = orc.limite_alerta || 10;
        let status = "ok";
        if (orc.tipo === "despesa") {
          if (desvio > limiteAlerta) status = "alerta";
          else if (desvio > 0) status = "atencao";
        } else {
          if (desvio < -limiteAlerta) status = "alerta";
          else if (desvio < 0) status = "atencao";
        }

        const dadosGrafico = mesesLabel.map((label, idx) => ({
          mes: label,
          orcado: orcadoMensal[idx],
          realizado: realizadoMensal[idx],
          diferenca: realizadoMensal[idx] - orcadoMensal[idx]
        }));

        return {
          ...orc,
          totalOrcado,
          totalRealizado,
          desvio,
          status,
          orcadoMensal,
          realizadoMensal,
          dadosGrafico
        };
      });
  }, [orcamentos, realizadoPorCategoria, ano]);

  // Resumo geral
  const resumo = useMemo(() => {
    const receitasOrcadas = comparativos.filter(c => c.tipo === 'receita').reduce((a, c) => a + c.totalOrcado, 0);
    const receitasRealizadas = comparativos.filter(c => c.tipo === 'receita').reduce((a, c) => a + c.totalRealizado, 0);
    const despesasOrcadas = comparativos.filter(c => c.tipo === 'despesa').reduce((a, c) => a + c.totalOrcado, 0);
    const despesasRealizadas = comparativos.filter(c => c.tipo === 'despesa').reduce((a, c) => a + c.totalRealizado, 0);

    const resultadoOrcado = receitasOrcadas - despesasOrcadas;
    const resultadoRealizado = receitasRealizadas - despesasRealizadas;

    const alertas = comparativos.filter(c => c.status === 'alerta').length;
    const atencao = comparativos.filter(c => c.status === 'atencao').length;

    return {
      receitasOrcadas, receitasRealizadas,
      despesasOrcadas, despesasRealizadas,
      resultadoOrcado, resultadoRealizado,
      alertas, atencao
    };
  }, [comparativos]);

  // Dados para gráfico consolidado mensal
  const dadosConsolidados = useMemo(() => {
    return mesesLabel.map((label, idx) => {
      const receitaOrcada = comparativos.filter(c => c.tipo === 'receita').reduce((a, c) => a + (c.orcadoMensal[idx] || 0), 0);
      const receitaRealizada = comparativos.filter(c => c.tipo === 'receita').reduce((a, c) => a + (c.realizadoMensal[idx] || 0), 0);
      const despesaOrcada = comparativos.filter(c => c.tipo === 'despesa').reduce((a, c) => a + (c.orcadoMensal[idx] || 0), 0);
      const despesaRealizada = comparativos.filter(c => c.tipo === 'despesa').reduce((a, c) => a + (c.realizadoMensal[idx] || 0), 0);

      return {
        mes: label,
        receitaOrcada,
        receitaRealizada,
        despesaOrcada,
        despesaRealizada,
        saldoOrcado: receitaOrcada - despesaOrcada,
        saldoRealizado: receitaRealizada - despesaRealizada
      };
    });
  }, [comparativos]);

  const statusConfig = {
    ok: { icon: CheckCircle, color: "text-green-600", bg: "bg-green-100", badge: "bg-green-600" },
    atencao: { icon: AlertTriangle, color: "text-amber-600", bg: "bg-amber-100", badge: "bg-amber-500" },
    alerta: { icon: AlertTriangle, color: "text-red-600", bg: "bg-red-100", badge: "bg-red-600" }
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 rounded-lg shadow-lg border border-stone-200">
          <p className="font-semibold text-stone-800 mb-2">{label}</p>
          {payload.map((entry, idx) => (
            <p key={idx} className="text-sm" style={{ color: entry.color }}>
              {entry.name}: {formatCurrency(entry.value)}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="space-y-6">
      {/* Cards Resumo */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
          <CardContent className="p-4">
            <p className="text-green-100 text-xs">Receitas Orçadas</p>
            <p className="text-xl font-bold">{formatCurrency(resumo.receitasOrcadas)}</p>
            <p className="text-xs text-green-200 mt-1">
              Realizado: {formatCurrency(resumo.receitasRealizadas)}
            </p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-red-500 to-red-600 text-white">
          <CardContent className="p-4">
            <p className="text-red-100 text-xs">Despesas Orçadas</p>
            <p className="text-xl font-bold">{formatCurrency(resumo.despesasOrcadas)}</p>
            <p className="text-xs text-red-200 mt-1">
              Realizado: {formatCurrency(resumo.despesasRealizadas)}
            </p>
          </CardContent>
        </Card>

        <Card className={`${resumo.resultadoOrcado >= 0 ? 'bg-gradient-to-br from-blue-500 to-blue-600' : 'bg-gradient-to-br from-stone-500 to-stone-600'} text-white`}>
          <CardContent className="p-4">
            <p className="text-white/70 text-xs">Resultado Orçado</p>
            <p className="text-xl font-bold">{formatCurrency(resumo.resultadoOrcado)}</p>
            <p className="text-xs text-white/60 mt-1">
              Realizado: {formatCurrency(resumo.resultadoRealizado)}
            </p>
          </CardContent>
        </Card>

        <Card className={`${resumo.alertas > 0 ? 'bg-gradient-to-br from-red-500 to-red-600' : resumo.atencao > 0 ? 'bg-gradient-to-br from-amber-500 to-amber-600' : 'bg-gradient-to-br from-green-500 to-green-600'} text-white`}>
          <CardContent className="p-4">
            <p className="text-white/70 text-xs">Desvios</p>
            <p className="text-xl font-bold">{resumo.alertas} alertas</p>
            <p className="text-xs text-white/60 mt-1">
              {resumo.atencao} em atenção
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Gráfico Consolidado */}
      <Card className="bg-white border-stone-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Target className="w-5 h-5 text-indigo-600" />
            Orçado vs Realizado - Consolidado Mensal
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={dadosConsolidados}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
                <XAxis dataKey="mes" tick={{ fontSize: 11 }} />
                <YAxis 
                  tickFormatter={(v) => new Intl.NumberFormat('pt-BR', { notation: 'compact' }).format(v)}
                  tick={{ fontSize: 11 }}
                />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Bar dataKey="receitaOrcada" name="Receita Orçada" fill="#bbf7d0" radius={[4, 4, 0, 0]} />
                <Bar dataKey="receitaRealizada" name="Receita Realizada" fill="#22c55e" radius={[4, 4, 0, 0]} />
                <Bar dataKey="despesaOrcada" name="Despesa Orçada" fill="#fecaca" radius={[4, 4, 0, 0]} />
                <Bar dataKey="despesaRealizada" name="Despesa Realizada" fill="#ef4444" radius={[4, 4, 0, 0]} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Lista de Comparativos por Orçamento */}
      <Card className="bg-white border-stone-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-600" />
            Acompanhamento por Categoria
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {comparativos.length === 0 ? (
            <p className="text-stone-500 text-center py-8">Nenhum orçamento cadastrado para {ano}</p>
          ) : (
            comparativos.map(comp => {
              const config = statusConfig[comp.status];
              const Icon = config.icon;
              const percentual = comp.totalOrcado > 0 ? (comp.totalRealizado / comp.totalOrcado) * 100 : 0;

              return (
                <div key={comp.id} className={`p-4 rounded-xl border ${config.bg} border-opacity-50`}>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <Icon className={`w-5 h-5 ${config.color}`} />
                      <div>
                        <p className="font-semibold text-stone-800">{comp.nome}</p>
                        <div className="flex gap-2 mt-1">
                          <Badge variant="outline" className="text-xs">
                            {comp.tipo === 'receita' ? '📈 Receita' : '📉 Despesa'}
                          </Badge>
                          <Badge variant="outline" className="text-xs">{comp.categoria}</Badge>
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <Badge className={`${config.badge} text-white`}>
                        {comp.desvio >= 0 ? '+' : ''}{comp.desvio.toFixed(1)}%
                      </Badge>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-4 mb-3">
                    <div>
                      <p className="text-xs text-stone-500">Orçado</p>
                      <p className="font-bold text-stone-800">{formatCurrency(comp.totalOrcado)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-stone-500">Realizado</p>
                      <p className={`font-bold ${comp.tipo === 'receita' ? 'text-green-700' : 'text-red-700'}`}>
                        {formatCurrency(comp.totalRealizado)}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-stone-500">Diferença</p>
                      <p className={`font-bold ${(comp.totalRealizado - comp.totalOrcado) >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                        {formatCurrency(comp.totalRealizado - comp.totalOrcado)}
                      </p>
                    </div>
                  </div>

                  <div className="space-y-1">
                    <div className="flex justify-between text-xs text-stone-500">
                      <span>Execução</span>
                      <span>{percentual.toFixed(1)}%</span>
                    </div>
                    <Progress 
                      value={Math.min(percentual, 100)} 
                      className={`h-2 ${percentual > 100 ? '[&>div]:bg-red-500' : '[&>div]:bg-green-500'}`}
                    />
                  </div>
                </div>
              );
            })
          )}
        </CardContent>
      </Card>
    </div>
  );
}